import { UserCardProps } from '@/types'
import { useEffect, useRef } from 'react'

export const UserCard: React.FC<UserCardProps> = ({
  name,
  avatar,
  streams,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const screenRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    // Copy the ref values to stable variables at the start of the effect
    const videoElement = videoRef.current
    const screenElement = screenRef.current

    // Handle video track
    const videoTrack = streams?.find((t) => t.type === 'video')
    if (videoElement && videoTrack?.track) {
      // Only update if the track has actually changed
      const currentStream = videoElement.srcObject as MediaStream
      const currentTrack = currentStream?.getVideoTracks()[0]

      if (currentTrack?.id !== videoTrack.track.id) {
        console.log('🎥 Updating video track:', {
          oldTrackId: currentTrack?.id,
          newTrackId: videoTrack.track.id,
        })

        const videoStream = new MediaStream([videoTrack.track])
        videoElement.srcObject = videoStream

        // Wait for the video to be ready before playing
        videoElement.onloadedmetadata = () => {
          videoElement.play().catch((err) => {
            console.error('Error playing video:', err)
            console.error('Video error details:', {
              name: err?.name,
              message: err?.message,
              code: err?.code,
              stack: err?.stack,
            })
          })
        }
      }
    } else if (videoElement && videoElement.srcObject) {
      // Clear the video element if no track
      console.log('🎥 Clearing video element')
      videoElement.srcObject = null
    }

    // Handle screen sharing track
    const screenTrack = streams?.find((t) => t.type === 'screen')
    if (screenElement && screenTrack?.track) {
      const screenStream = new MediaStream([screenTrack.track])
      screenElement.srcObject = screenStream
      screenElement
        .play()
        .catch((err) => console.error('Error playing screen share:', err))
    } else if (screenElement) {
      // Clear the screen element if no track
      screenElement.srcObject = null
    }

    // Cleanup function
    return () => {
      if (videoElement) {
        videoElement.srcObject = null
      }
      if (screenElement) {
        screenElement.srcObject = null
      }
    }
  }, [streams])

  return (
    <div className="bg-gray-800 rounded-lg p-4 flex flex-col items-center text-white">
      {/* Avatar and Name */}
      <img
        src={avatar}
        alt={`${name}'s avatar`}
        className="w-20 h-20 rounded-full object-cover mb-4"
      />
      <h3 className="text-white text-sm">{name}</h3>

      {/* Video Stream */}
      {streams?.some((t) => t.type === 'video') ? (
        <video
          ref={videoRef}
          className="w-full h-32 rounded-md mt-2"
          muted // Ensure no echo for local streams
        />
      ) : (
        <p className="text-gray-400 text-xs">No video available</p>
      )}

      {/* Screen Share */}
      {streams?.some((t) => t.type === 'screen') && (
        <div className="w-full h-32 rounded-md mt-2 border border-blue-400 relative">
          <video
            ref={screenRef}
            className="absolute top-0 left-0 w-full h-full object-cover"
            muted
          />
          <span className="absolute bottom-2 right-2 bg-blue-500 text-xs text-white px-2 py-1 rounded">
            Screen Sharing
          </span>
        </div>
      )}

      {/* Audio Indicator */}
      {streams?.some((t) => t.type === 'audio') ? (
        <p className="text-green-400 text-xs mt-2">Audio Active</p>
      ) : (
        <p className="text-gray-400 text-xs mt-2">No audio available</p>
      )}
    </div>
  )
}
